import { useQuery } from "@tanstack/react-query";
import type {
  AgentUsageSummary,
  FlowSpend,
  LedgerEntry,
  LedgerFilter,
  Wallet,
  ZotRoutedTool,
  ZotToolUsage,
} from "@zworks/console-contracts";
import * as React from "react";
import { Link } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { CertaintyBadge, PageHeader, SpendMeter } from "@/components/bespoke";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ApiError,
  ledgerQuery,
  runsQuery,
  walletQuery,
  zotToolUsageQuery,
  zotUsageQuery,
} from "@/lib/api";
import {
  formatAmount,
  formatCredits,
  formatCreditsUsd,
  formatDate,
  formatEntryDate,
} from "@/lib/format";
import { entryDetail, formatAmountUsd, ledgerCsv, spendByFlow } from "@/lib/ledger";
import { useCurrentProject } from "@/lib/project";
import { AskButton } from "@/components/ask-zot";
import { UsageSpend } from "./usage-spend";

const filters: { value: LedgerFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "purchases", label: "Purchases" },
  { value: "usage", label: "Usage" },
];

const kindLabels: Record<LedgerEntry["kind"], string> = {
  reserve: "Reserve",
  consume: "Consume",
  release: "Release",
  purchase: "Purchase",
  grant: "Grant",
  refund: "Refund",
  unknown: "Unknown",
  // A `resolution` entry is the owner settling a run's unresolved spend; showing it
  // as a ledger row is what lets a reader see the unknown and its answer together.
  resolution: "Resolution",
  metered: "BYOK usage",
};

/** Saves the CSV to a download, named for the project for filing. */
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * The failure, with the request id for support. A 403 clears the content first and explains
 * second, so stale rows cannot survive the response.
 */
function UsageError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (error instanceof ApiError && error.status === 403) {
    return (
      <ForbiddenState
        title="You do not have access to this project"
        description="Ask an owner to invite you. Nothing from the project is shown."
      />
    );
  }
  const apiError = error instanceof ApiError ? error : null;
  return (
    <ErrorState
      title="Usage could not be loaded"
      description={apiError?.problem?.detail ?? "The request could not be completed."}
      requestId={apiError?.requestId ?? "unknown"}
      onRetry={onRetry}
    />
  );
}

function BalanceCards({ wallet }: { wallet: Wallet }) {
  const used = wallet.monthlyUsed;
  const limit = wallet.monthlyLimit;
  return (
    <section aria-label="Balance and limits" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Available credits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-semibold tracking-tight tabular-nums">
              {formatCredits(wallet.available)}
            </p>
            <span className="text-sm font-medium text-muted-foreground tabular-nums">
              ({formatCreditsUsd(wallet.available)})
            </span>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Monthly spending limit
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-semibold tracking-tight tabular-nums">
              {formatCredits(limit)}
            </p>
            <span className="text-sm font-medium text-muted-foreground tabular-nums">
              ({formatCreditsUsd(limit)})
            </span>
          </div>
          <SpendMeter used={used} reserved={wallet.reserved} limit={limit} showLabels={false} />
          <p className="mt-1 text-[0.8125rem] text-muted-foreground tabular-nums">
            {formatCredits(used)} of {formatCredits(limit)} used ({formatCreditsUsd(used)} of{" "}
            {formatCreditsUsd(limit)}), resets {formatDate(wallet.resetsAt)}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">Per-run cap</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-semibold tracking-tight tabular-nums">
              {formatCredits(wallet.perRunCap)}
            </p>
            <span className="text-sm font-medium text-muted-foreground tabular-nums">
              ({formatCreditsUsd(wallet.perRunCap)})
            </span>
          </div>
          <p className="mt-1 text-[0.8125rem] text-muted-foreground">
            A run holds this much at most.
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Confirmed spend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-semibold tracking-tight tabular-nums">
              {formatCredits(wallet.confirmed)}
            </p>
            <span className="text-sm font-medium text-muted-foreground tabular-nums">
              ({formatCreditsUsd(wallet.confirmed)})
            </span>
          </div>
          <p className="mt-1 text-[0.8125rem] text-muted-foreground">Billed and reconciled.</p>
        </CardContent>
      </Card>
    </section>
  );
}

function AssistantUsageCard({ usage }: { usage: AgentUsageSummary | undefined }) {
  if (!usage) return null;
  const byok = usage.modelAccess === "byok";
  return (
    <section aria-label="Assistant usage">
      <Card>
        <CardHeader>
          <CardTitle>Assistant chat</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-sm font-medium">Cost to you</dt>
              <dd className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">
                {byok ? "Not metered under BYOK" : "Free under fair use"}
              </dd>
              <dd className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                {byok
                  ? "Model cost is on your own key."
                  : "Chats use no credits while the fair-use cap holds."}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium">Project tokens</dt>
              <dd className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">
                {formatCredits(usage.projectTokens)}
              </dd>
              <dd className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                of {formatCredits(usage.fairUseCapTokens)} fair-use tokens
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium">Your tokens in this project</dt>
              <dd className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-semibold tracking-tight tabular-nums">
                  {formatCredits(usage.userTokens)}
                </span>
                {usage.isCapExceeded ? <CertaintyBadge certainty="unknown" /> : null}
              </dd>
              <dd className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                {usage.isCapExceeded
                  ? "New chats are refused until usage falls under the cap."
                  : usage.costDisplay}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </section>
  );
}

const toolLabels: Record<ZotRoutedTool, string> = {
  answer: "Answer",
  room: "Room",
  studio: "Studio",
  run: "Run",
  work: "Work items",
  approvals: "Approvals",
  usage: "Usage",
  automation: "Automation",
  integrations: "Integrations",
  market: "Marketplace",
  evidence: "Evidence",
  project: "Project",
};

const usageHeadClass =
  "h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5";
const usageCellClass =
  "border-b px-3 py-2.5 text-right tabular-nums first:pl-5 first:text-left last:pr-5";

/** Assistant tool calls, proposal outcomes, and settled credits, per tool and per chat. */
function AssistantToolsCard({
  usage,
  failed,
}: {
  usage: ZotToolUsage | undefined;
  failed: boolean;
}) {
  if (failed) {
    return (
      <section aria-label="Assistant tools">
        <Card>
          <CardHeader>
            <CardTitle>Assistant tools</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Assistant tool usage could not be loaded.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }
  if (!usage) return null;
  const confirmed = usage.tools.reduce((sum, row) => sum + row.confirmed, 0);
  const awaiting = usage.tools.reduce((sum, row) => sum + row.awaiting, 0);
  return (
    <section aria-label="Assistant tools">
      <Card>
        <CardHeader>
          <CardTitle>Assistant tools</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6">
          {usage.tools.length === 0 ? (
            <EmptyState
              title="No assistant tool calls yet"
              description="Each tool Zot routes to, its proposals, and their settled credits appear here."
            />
          ) : (
            <>
              <div className="flex flex-wrap gap-2" aria-label="Proposal outcomes">
                <Badge variant="success">{confirmed} confirmed</Badge>
                <Badge variant="muted">{awaiting} not yet confirmed</Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">Assistant tool calls by tool</caption>
                  <thead>
                    <tr>
                      <th scope="col" className={usageHeadClass}>
                        Tool
                      </th>
                      <th scope="col" className={`${usageHeadClass} text-right`}>
                        Calls
                      </th>
                      <th scope="col" className={`${usageHeadClass} text-right`}>
                        Refused
                      </th>
                      <th scope="col" className={`${usageHeadClass} text-right`}>
                        Confirmed
                      </th>
                      <th scope="col" className={`${usageHeadClass} text-right`}>
                        Tokens
                      </th>
                      <th scope="col" className={`${usageHeadClass} text-right`}>
                        Credits
                      </th>
                      <th scope="col" className={`${usageHeadClass} text-right`}>
                        Own-key tasks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.tools.map((row) => (
                      <tr key={row.tool}>
                        <th
                          scope="row"
                          className="border-b px-3 py-2.5 text-left font-medium first:pl-5"
                        >
                          {toolLabels[row.tool]}
                        </th>
                        <td className={usageCellClass}>{formatCredits(row.calls)}</td>
                        <td className={usageCellClass}>{formatCredits(row.refused)}</td>
                        <td className={usageCellClass}>{formatCredits(row.confirmed)}</td>
                        <td className={usageCellClass}>{formatCredits(row.tokens)}</td>
                        <td className={usageCellClass}>{formatCredits(row.creditsSettled)}</td>
                        <td className={usageCellClass}>{formatCredits(row.byokTasks)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {usage.chats.length === 0 ? null : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <caption className="sr-only">Assistant chats, newest first</caption>
                    <thead>
                      <tr>
                        <th scope="col" className={usageHeadClass}>
                          Chat
                        </th>
                        <th scope="col" className={`${usageHeadClass} text-right`}>
                          Calls
                        </th>
                        <th scope="col" className={`${usageHeadClass} text-right`}>
                          Confirmed
                        </th>
                        <th scope="col" className={`${usageHeadClass} text-right`}>
                          Tokens
                        </th>
                        <th scope="col" className={`${usageHeadClass} text-right`}>
                          Credits
                        </th>
                        <th scope="col" className={`${usageHeadClass} text-right`}>
                          Own-key tasks
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.chats.map((row) => (
                        <tr key={row.threadId}>
                          <th
                            scope="row"
                            className="border-b px-3 py-2.5 text-left font-normal first:pl-5"
                          >
                            <span className="flex flex-wrap items-center gap-2">
                              <span>Started {formatEntryDate(row.startedAt)}</span>
                              {row.mine ? <Badge variant="info">Yours</Badge> : null}
                            </span>
                          </th>
                          <td className={usageCellClass}>{formatCredits(row.calls)}</td>
                          <td className={usageCellClass}>{formatCredits(row.confirmed)}</td>
                          <td className={usageCellClass}>{formatCredits(row.tokens)}</td>
                          <td className={usageCellClass}>{formatCredits(row.creditsSettled)}</td>
                          <td className={usageCellClass}>{formatCredits(row.byokTasks)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[0.8125rem] text-muted-foreground">
                Credits are settled so far. Tasks on your own model key are not metered, so they add
                no credits.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function CertaintyCards({ wallet }: { wallet: Wallet }) {
  return (
    <section aria-label="Spending by certainty">
      <Card>
        <CardHeader>
          <CardTitle>How sure the figures are</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <dt className="flex items-center gap-1.5 text-sm font-medium">
                Confirmed <CertaintyBadge certainty="confirmed" />
              </dt>
              <dd className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-semibold tracking-tight tabular-nums">
                  {formatCredits(wallet.confirmed)}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  ({formatCreditsUsd(wallet.confirmed)})
                </span>
              </dd>
              <dd className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                Billed and reconciled.
              </dd>
            </div>
            <div>
              <dt className="flex items-center gap-1.5 text-sm font-medium">
                Reserved <CertaintyBadge certainty="reserved" />
              </dt>
              <dd className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-semibold tracking-tight tabular-nums">
                  {formatCredits(wallet.reserved)}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  ({formatCreditsUsd(wallet.reserved)})
                </span>
              </dd>
              <dd className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                Held for runs in progress.
              </dd>
            </div>
            <div>
              <dt className="flex items-center gap-1.5 text-sm font-medium">
                Estimated <CertaintyBadge certainty="estimated" />
              </dt>
              <dd className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-semibold tracking-tight tabular-nums">
                  {formatCredits(wallet.estimated)}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  ({formatCreditsUsd(wallet.estimated)})
                </span>
              </dd>
              <dd className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                Usage not yet reported.
              </dd>
            </div>
            <div>
              <dt className="flex items-center gap-1.5 text-sm font-medium">
                Unknown <CertaintyBadge certainty="unknown" />
              </dt>
              <dd className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">
                {wallet.unknownCount === 0 ? "None" : `${wallet.unknownCount} items`}
              </dd>
              <dd className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                No amount can be stated yet.
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </section>
  );
}

function UnknownAlert({ wallet, entries }: { wallet: Wallet; entries: LedgerEntry[] }) {
  if (wallet.unknownCount === 0) return null;
  const first = entries.find((entry) => entry.kind === "unknown" && entry.runId);
  return (
    <div
      role="alert"
      className="flex gap-3 rounded-[var(--radius-card)] border border-dashed border-unknown/60 bg-unknown/6 p-3 text-unknown"
    >
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium text-foreground">
          {wallet.unknownCount === 1 ? "1 item" : `${wallet.unknownCount} items`} cannot be stated
          yet
        </p>
        <p className="text-[0.8125rem] text-muted-foreground">
          {first?.note ??
            "A provider call has not confirmed. It stays listed as unknown until reconciled, and cannot be deleted or edited here."}
        </p>
        {first?.runId ? (
          <p className="text-[0.8125rem]">
            <Link
              to={`/runs/${first.runId}`}
              className="text-accent-text underline-offset-4 hover:underline"
            >
              Open run
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  );
}

function FlowSpendTable({ flows }: { flows: FlowSpend[] }) {
  if (flows.length === 0) {
    return (
      <EmptyState
        title="No settled spend yet"
        description="Settled runs group here by flow, newest month first."
      />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">Settled spend by flow this month</caption>
        <thead>
          <tr>
            <th
              scope="col"
              className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
            >
              Flow
            </th>
            <th
              scope="col"
              className="h-10 border-b px-3 text-right text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
            >
              Credits
            </th>
          </tr>
        </thead>
        <tbody>
          {flows.map((flow) => (
            <tr key={flow.flowVersion ?? "unlabelled"} className="group hover:bg-muted/50">
              <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]">
                {flow.flowVersion ?? "Unlabelled"}
              </td>
              <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-right tabular-nums">
                <span>{formatCredits(flow.credits)}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  ({formatCreditsUsd(flow.credits)})
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LedgerTable({ entries }: { entries: LedgerEntry[] }) {
  if (entries.length === 0) {
    return (
      <EmptyState
        title="No entries yet"
        description="Reserves, spend, purchases, grants, refunds, and customer-key usage appear here, newest first."
      />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">Ledger entries, newest first</caption>
        <thead>
          <tr>
            <th
              scope="col"
              className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
            >
              When
            </th>
            <th
              scope="col"
              className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
            >
              Entry
            </th>
            <th
              scope="col"
              className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
            >
              Detail
            </th>
            <th
              scope="col"
              className="h-10 border-b px-3 text-right text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
            >
              Credits
            </th>
            <th
              scope="col"
              className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
            >
              Certainty
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="group hover:bg-muted/50">
              <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
                {formatEntryDate(entry.createdAt)}
              </td>
              <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                {kindLabels[entry.kind]}
              </td>
              <td className="border-b px-3 py-2.5 align-middle group-last:border-b-0 first:pl-5 last:pr-5">
                {entryDetail(entry)}
              </td>
              <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-right tabular-nums">
                <span>{formatAmount(entry.amount, entry.kind)}</span>
                {entry.kind !== "unknown" ? (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({formatAmountUsd(entry)})
                  </span>
                ) : null}
              </td>
              <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                <CertaintyBadge certainty={entry.certainty} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The usage page, as the Usage board draws it: the available balance, the monthly limit with
 * its reset date, the four labelled certainty totals, the unknown alert, the ledger table
 * with its tabs, and the CSV export. Spend by flow reads from the runs list, since flows gain
 * their own tables in a later task.
 */
export function UsagePage() {
  const { project, loaded } = useCurrentProject();
  const [lens, setLens] = React.useState<"developer" | "cto">("developer");
  const [filter, setFilter] = React.useState<LedgerFilter>("all");
  const [page, setPage] = React.useState(1);
  const wallet = useQuery({
    ...walletQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  const ledger = useQuery({
    ...ledgerQuery(project?.id ?? "", filter, page),
    enabled: loaded && project !== null,
  });
  const assistantUsage = useQuery({
    ...zotUsageQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  // Tool telemetry loads beside the wallet: a failure shows in its own card and never blocks the page.
  const toolUsage = useQuery({
    ...zotToolUsageQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });
  // Spend by flow reads the settled runs, since flows gain their own tables in a later task.
  // It loads quietly beside the ledger: the page never blocks on it.
  const flows = useQuery({
    ...runsQuery(project?.id ?? "", "all", 1),
    enabled: loaded && project !== null,
  });

  function selectFilter(next: LedgerFilter) {
    setFilter(next);
    setPage(1);
  }

  // A disabled query stays pending, so the empty project is decided before the queries.
  if (!loaded || (project !== null && (wallet.isPending || ledger.isPending))) {
    return (
      <>
        <PageHeader title="Usage" />
        <LoadingState label="Loading usage" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <PageHeader title="Usage" />
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its usage appears here."
        />
      </>
    );
  }

  if (wallet.isError || ledger.isError) {
    const error = wallet.error ?? ledger.error;
    return (
      <>
        <PageHeader title="Usage" description={project.name} />
        <UsageError
          error={error}
          onRetry={() => {
            void wallet.refetch();
            void ledger.refetch();
          }}
        />
      </>
    );
  }

  // The project switch can disable the queries mid-flight, so data is read once it exists.
  const walletData = wallet.data;
  const ledgerData = ledger.data;
  if (!walletData || !ledgerData) {
    return (
      <>
        <PageHeader title="Usage" description={project.name} />
        <LoadingState label="Loading usage" rows={6} />
      </>
    );
  }

  const totalPages = Math.max(1, Math.ceil(ledgerData.total / ledgerData.pageSize));
  const currentPage = Math.min(page, totalPages);
  const from = ledgerData.total === 0 ? 0 : (currentPage - 1) * ledgerData.pageSize + 1;
  const to = (currentPage - 1) * ledgerData.pageSize + ledgerData.entries.length;
  const flowSpend = flows.data ? spendByFlow(flows.data.runs, new Date()) : null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Usage"
        actions={
          <div className="flex flex-wrap gap-2">
            <div
              role="group"
              aria-label="Usage lens"
              className="flex gap-1 rounded-full border p-1"
            >
              <Button
                size="sm"
                variant={lens === "developer" ? "default" : "ghost"}
                aria-pressed={lens === "developer"}
                onClick={() => setLens("developer")}
              >
                Developer
              </Button>
              <Button
                size="sm"
                variant={lens === "cto" ? "default" : "ghost"}
                aria-pressed={lens === "cto"}
                onClick={() => setLens("cto")}
              >
                CTO
              </Button>
            </div>
            <AskButton kind="usage" id={project.id} label={project.name} variant="outline" />
            <Button
              variant="outline"
              onClick={() =>
                downloadCsv(`${project.slug}-ledger.csv`, ledgerCsv(ledgerData.entries))
              }
            >
              Export CSV
            </Button>
            <Button asChild>
              <Link to="/checkout">Buy credits</Link>
            </Button>
          </div>
        }
      />

      <BalanceCards wallet={walletData} />
      <UsageSpend projectId={project.id} wallet={walletData} />
      {lens === "cto" ? (
        <Card>
          <CardHeader>
            <CardTitle>Project spend and controls</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">Project</p>
              <p className="font-medium">{project.name}</p>
              <Link className="text-sm underline underline-offset-4" to="/settings/general">
                View project details
              </Link>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Spend this month</p>
              <p className="font-medium tabular-nums">{formatCreditsUsd(walletData.monthlyUsed)}</p>
              <Button
                variant="ghost"
                className="h-auto p-0 text-accent-text underline underline-offset-4"
                onClick={() => setLens("developer")}
              >
                View ledger evidence
              </Button>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Held by running work</p>
              <p className="font-medium tabular-nums">{formatCreditsUsd(walletData.reserved)}</p>
              <Link className="text-sm underline underline-offset-4" to="/runs">
                View running work
              </Link>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly limit</p>
              <p className="font-medium tabular-nums">
                {formatCreditsUsd(walletData.monthlyLimit)}
              </p>
              <Link className="text-sm underline underline-offset-4" to="/settings/spend">
                View budget controls
              </Link>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Per-run cap</p>
              <p className="font-medium tabular-nums">{formatCreditsUsd(walletData.perRunCap)}</p>
              <Link className="text-sm underline underline-offset-4" to="/settings/spend">
                View budget controls
              </Link>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Runs inside policy</p>
              <p className="font-medium">Not connected yet</p>
              <Link className="text-sm underline underline-offset-4" to="/settings/agents">
                View agent controls
              </Link>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Median approval time</p>
              <p className="font-medium">Not connected yet</p>
              <Link className="text-sm underline underline-offset-4" to="/approvals">
                View approval evidence
              </Link>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Evidence coverage</p>
              <p className="font-medium">Not connected yet</p>
              <Link className="text-sm underline underline-offset-4" to="/audit">
                View audit evidence
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <AssistantUsageCard usage={assistantUsage.data} />
      )}
      <AssistantToolsCard usage={toolUsage.data} failed={toolUsage.isError} />
      <CertaintyCards wallet={walletData} />
      <UnknownAlert wallet={walletData} entries={ledgerData.entries} />

      {lens === "developer" ? (
        <section aria-label="Ledger">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle>Ledger</CardTitle>
                <Tabs value={filter} onValueChange={(value) => selectFilter(value as LedgerFilter)}>
                  <TabsList aria-label="Filter ledger entries">
                    {filters.map((entry) => (
                      <TabsTrigger key={entry.value} value={entry.value}>
                        {entry.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <LedgerTable entries={ledgerData.entries} />
            <div className="flex items-center justify-between gap-3 border-t px-5 py-3">
              <p className="text-[0.8125rem] text-muted-foreground tabular-nums">
                {from}–{to} of {ledgerData.total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        </section>
      ) : null}

      <section aria-label="Spend by flow">
        <Card>
          <CardHeader>
            <CardTitle>By flow this month</CardTitle>
          </CardHeader>
          {flowSpend === null ? (
            <CardContent>
              <LoadingState label="Loading spend by flow" rows={3} />
            </CardContent>
          ) : (
            <FlowSpendTable flows={flowSpend} />
          )}
        </Card>
      </section>
    </div>
  );
}
